import React, { useState } from "react";
import {
  TextField, Button, Grid, Card, CardContent, Typography,
  MenuItem, CircularProgress, Divider, Box, Container, Alert
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PersonIcon from "@mui/icons-material/Person";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PaymentIcon from "@mui/icons-material/Payment";
import FileUploadIcon from "@mui/icons-material/FileUpload";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { applicationSchema } from "../validationSchema";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";

const BUCKET_NAME = "gmobiles"; // bucket for file uploads
const TABLE_NAME = "applications"; // your database table

const SectionCard = ({ title, icon: Icon, children }) => (
  <Card sx={{ mb: 4 }}>
    <CardContent sx={{ p: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 1.5 }}>
        <Icon sx={{ color: "#0D47A1" }} />
        <Typography variant="h6">{title}</Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />
      <Grid container spacing={3}>{children}</Grid>
    </CardContent>
  </Card>
);

const ApplicationForm = () => {
  const navigate = useNavigate();
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: yupResolver(applicationSchema),
    defaultValues: {
      name: "", fatherName: "", motherName: "", wifeName: "",
      mobile: "", whatsappMobile: "", email: "", address: "",
      birthdate: "", aadharNo: "", panNo: "", voterId: "",
      drivingLicense: "", job: "", duration: "", amount: "",
      purchaseType: "", paymentStatus: "active"
    }
  });

  const [files, setFiles] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 5 * 1024 * 1024) {
      alert("Max file size 5MB");
      return;
    }
    setFiles(prev => ({ ...prev, [e.target.name]: file }));
  };

  const onSubmit = async (data) => {
    setLoading(true);
    setError(null);

    try {
      // Duplicate mobile check
      const { data: existing, error: selectError } = await supabase
        .from(TABLE_NAME)
        .select("id")
        .eq("mobile", data.mobile)
        .maybeSingle();

      if (selectError) throw selectError;
      if (existing) throw new Error("Mobile number already exists");

      const uploaded = {};

      for (let key of Object.keys(files)) {
        if (files[key]) {
          const fileName = `${Date.now()}-${files[key].name}`;
          const { error: uploadError } = await supabase.storage
            .from(BUCKET_NAME)
            .upload(fileName, files[key]);

          if (uploadError) throw uploadError;

          const { data: publicData, error: publicError } = await supabase.storage
            .from(BUCKET_NAME)
            .getPublicUrl(fileName);

          if (publicError) throw publicError;
          uploaded[key] = publicData.publicUrl;
        }
      }

      const { error } = await supabase.from(TABLE_NAME).insert([{
        name: data.name,
        father_name: data.fatherName,
        mother_name: data.motherName,
        spouse_name: data.wifeName,
        mobile: data.mobile,
        whatsapp: data.whatsappMobile,
        email: data.email,
        address: data.address,
        birthdate: data.birthdate,
        aadhar: data.aadharNo,
        pan: data.panNo,
        voter_id: data.voterId,
        driving_license: data.drivingLicense,
        job: data.job,
        duration: data.duration,
        amount: data.amount,
        purchase_type: data.purchaseType,
        file1: uploaded.file1 || null,
        file2: uploaded.file2 || null,
        file3: uploaded.file3 || null,
        file4: uploaded.file4 || null,
        file5: uploaded.file5 || null,
      }]);

      if (error) throw error;

      alert("Application submitted successfully!");
      reset();
      setFiles({});
      navigate("/");

    } catch (err) {
      console.error("Form submission error:", err);
      // Supabase error objects sometimes contain additional fields
      let msg = "An error occurred. Please try again.";
      if (err.message) msg = err.message;
      if (err.details) msg += ` - ${err.details}`;
      if (err.hint) msg += ` (${err.hint})`;
      // row-level security violation hint
      if (msg.toLowerCase().includes("row-level security")) {
        msg +=
          "\n\nIt looks like your Supabase table has RLS enabled and the anon key doesn\'t have permission to insert. Please adjust the RLS policy to allow inserts or disable RLS. See dashboard > Table > RLS policies.";
      }
      setError(msg);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: "#F5F7FA", minHeight: "100vh", py: 6 }}>
      <Container maxWidth="md">
        {error && (
          <Alert 
            severity="error" 
            onClose={() => setError(null)}
            sx={{ mb: 3, fontSize: "1rem" }}
          >
            {error}
          </Alert>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          <SectionCard title="Personal Details" icon={PersonIcon}>
            {[
              { name: "name", label: "Name *", required: true },
              { name: "fatherName", label: "Father Name" },
              { name: "motherName", label: "Mother Name"},
              { name: "wifeName", label: "Spouse Name" },
              { name: "birthdate", label: "Birthdate", type: "date"},
              { name: "mobile", label: "Mobile *", required: true },
              { name: "whatsappMobile", label: "WhatsApp Mobile" },
              { name: "email", label: "Email"},
              { name: "address", label: "Address"}
            ].map(field => (
              <Grid item xs={12} md={6} key={field.name}>
                <TextField
                  fullWidth
                  label={field.label}
                  type={field.type || "text"}
                  InputLabelProps={field.type === "date" ? { shrink: true } : {}}
                  {...register(field.name)}
                  error={!!errors[field.name]}
                  helperText={errors[field.name]?.message}
                />
              </Grid>
            ))}
          </SectionCard>

          <SectionCard title="Identification" icon={VerifiedUserIcon}>
            {[
              { name: "aadharNo", label: "Aadhar No (12 digits)" },
              { name: "panNo", label: "PAN (10 characters)" },
              { name: "voterId", label: "Voter ID" },
              { name: "drivingLicense", label: "Driving License" }
            ].map(field => (
              <Grid item xs={12} md={6} key={field.name}>
                <TextField 
                  fullWidth 
                  label={field.label} 
                  {...register(field.name)}
                  error={!!errors[field.name]}
                  helperText={errors[field.name]?.message}
                />
              </Grid>
            ))}
          </SectionCard>

          <SectionCard title="Employment & Finance" icon={PaymentIcon}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Job" {...register("job")} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Duration" {...register("duration")} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField 
                select 
                fullWidth 
                label="Purchase Type" 
                {...register("purchaseType")}
                error={!!errors.purchaseType}
                helperText={errors.purchaseType?.message}
              >
                <MenuItem value="">Select Purchase Type</MenuItem>
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="tvs">TVS</MenuItem>
                <MenuItem value="bajaj">Bajaj</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField 
                fullWidth 
                label="Amount" 
                type="number" 
                {...register("amount")}
                error={!!errors.amount}
                helperText={errors.amount?.message}
              />
            </Grid>
          </SectionCard>

          <SectionCard title="Upload Files" icon={FileUploadIcon}>
            {[1,2,3,4,5].map(num => (
              <Grid item xs={12} md={6} key={num}>
                <Button component="label" fullWidth variant="outlined">
                  {files[`file${num}`]?.name || `Upload File ${num}`}
                  <input hidden type="file" name={`file${num}`} onChange={handleFileChange}/>
                </Button>
              </Grid>
            ))}
          </SectionCard>

          <Box textAlign="center" mt={4}>
            {Object.keys(errors).length > 0 && (
              <Alert severity="warning" sx={{ mb: 2, textAlign: "left" }}>
                <strong>Please fix the following errors:</strong>
                <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                  {Object.entries(errors).map(([field, error]) => (
                    <li key={field}>{field}: {error?.message}</li>
                  ))}
                </ul>
              </Alert>
            )}
            <Button type="submit" variant="contained" disabled={loading} size="large">
              {loading ? <CircularProgress size={24}/> : "Submit"}
            </Button>
          </Box>
        </form>
      </Container>
    </Box>
  );
};

export default ApplicationForm;
